import axios from "axios";
import { API_BASE_PATH } from "../environment";
import { toast } from "react-toastify";

// ✅ Base URL
const BASE_URL = `${API_BASE_PATH}`;

// ✅ Get Auth Headers
const getAuthHeaders = () => {
  const token = localStorage.getItem("authToken") || "";
  return {
    Authorization: token,
    Accept: "application/json",
    "Content-Type": "application/x-www-form-urlencoded",
  };
};


// ✅ Error handler
const handleAxiosError = (
  error: any,
  fallbackMessage = "Something went wrong"
) => {
  if (axios.isAxiosError(error)) {
    const message =
      error.response?.data?.details ||
      error.response?.data?.message ||
      fallbackMessage;

    toast.error(message);
  } else {
    toast.error(fallbackMessage);
  }
};

// ✅ LeadType Interface
export interface LeadType {
  leadtype_id: string;
  leadtype_name: string;
  status?: string;
  created_at?: string;
}

// ✅ Create LeadType
export const createLeadType = async (
  leadtypeData: { leadtype_name: string; leadtype_status?: string }
): Promise<LeadType | null> => {
  try {
    const response = await axios.post(
      `${BASE_URL}/leadtype/create`,
      new URLSearchParams(leadtypeData),
      { headers: getAuthHeaders() }
    );
    toast.success("Lead Type created successfully");
    return response.data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.details || error?.response?.data?.message || "Failed to create Lead Type";
    toast.error(errorMessage);
    return null;
  }
};

// ✅ Update LeadType
export const updateLeadType = async (
  leadtype_id: string,
  leadtypeData: { leadtype_name: string; leadtype_status?: string }
): Promise<LeadType | null> => {
  try {
    const response = await axios.put(
      `${BASE_URL}/leadtype/update/${leadtype_id}`,
      new URLSearchParams(leadtypeData),
      { headers: getAuthHeaders() }
    );
    toast.success("Lead Type updated successfully");
    return response.data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.details || error?.response?.data?.message || "Failed to update Lead Type";
    toast.error(errorMessage);
    return null;
  }
};

// ✅ Get All LeadTypes
export const fetchAllLeadTypes = async (): Promise<LeadType[]> => {
  try {
    const response = await axios.get(`${BASE_URL}/leadtype/`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.details || error?.response?.data?.message || "Failed to fetch Lead Type";
    toast.error(errorMessage);
    return [];
  }
};

// ✅ Get All LeadTypes
export const fetchAllLeadTypesActive = async (): Promise<LeadType[]> => {
  try {
    const response = await axios.get(`${BASE_URL}/leadtype/active/`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.details || error?.response?.data?.message || "Failed to fetch Lead Type";
    toast.error(errorMessage);
    return [];
  }
};

// ✅ Get LeadType By ID
export const fetchLeadTypeById = async (
  leadtype_id: string
): Promise<LeadType | null> => {
  try {
    const response = await axios.get(`${BASE_URL}/leadtype/${leadtype_id}`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.details || error?.response?.data?.message || "Lead Type not found";
    toast.error(errorMessage);
    return null;
  }
};

// ✅ Delete LeadType
export const deleteLeadType = async (leadtype_id: string): Promise<boolean> => {
  try {
    await axios.delete(`${BASE_URL}/leadtype/delete/${leadtype_id}`, {
      headers: getAuthHeaders(),
    });
    toast.success("Lead Type deleted successfully");
    return true;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.details || error?.response?.data?.message || "Failed to delete Lead Type.";
    toast.error(errorMessage);
    return false;
  }
};

/* =====================================================
   EXPORT – ALL
===================================================== */

// ✅ Export all Lead Types – Excel
export const exportLeadTypeExcel = async (): Promise<Blob> => {
  try {
    const res = await axios.get(
      `${BASE_URL}/leadtype_export_all_excel`,
      {
        responseType: "blob",
        headers: getAuthHeaders(),
      }
    );

    toast.success("Lead types exported as Excel successfully");
    return res.data;
  } catch (error) {
    handleAxiosError(error, "Failed to export lead types (Excel)");
    throw error;
  }
};

// ✅ Export all Lead Types – PDF
export const exportLeadTypePDF = async (): Promise<Blob> => {
  try {
    const res = await axios.get(
      `${BASE_URL}/leadtype_export_all_pdf`,
      {
        responseType: "blob",
        headers: getAuthHeaders(),
      }
    );

    toast.success("Lead types exported as PDF successfully");
    return res.data;
  } catch (error) {
    handleAxiosError(error, "Failed to export lead types (PDF)");
    throw error;
  }
};

/* =====================================================
   EXPORT – SELECTED
===================================================== */

// ✅ Export selected Lead Types – Excel
export const exportSelectedLeadTypeExcel = async (
  ids: string[]
): Promise<Blob> => {
  try {
    const res = await axios.get(
      `${BASE_URL}/leadtype_export_selected_excel`,
      {
        params: { ids: ids.join(",") },
        responseType: "blob",
        headers: getAuthHeaders(),
      }
    );

    toast.success("Selected lead types exported as Excel successfully");
    return res.data;
  } catch (error) {
    handleAxiosError(error, "Failed to export selected lead types (Excel)");
    throw error;
  }
};

// ✅ Export selected Lead Types – PDF
export const exportSelectedLeadTypePDF = async (
  ids: string[]
): Promise<Blob> => {
  try {
    const res = await axios.get(
      `${BASE_URL}/leadtype_export_selected_pdf`,
      {
        params: { ids: ids.join(",") },
        responseType: "blob",
        headers: getAuthHeaders(),
      }
    );

    toast.success("Selected lead types exported as PDF successfully");
    return res.data;
  } catch (error) {
    handleAxiosError(error, "Failed to export selected lead types (PDF)");
    throw error;
  }
};

/* =====================================================
   BULK DELETE
===================================================== */

// ✅ Bulk delete Lead Types
export const deleteLeadTypeBulk = async (
  ids: string[]
): Promise<void> => {
  try {
    const res = await axios.delete(
      `${BASE_URL}/leadtype_bulk_delete`,
      {
        params: { ids: ids.join(",") },
        headers: getAuthHeaders(),
      }
    );

    //toast.success(res.data?.message || "Lead types deleted successfully");
  } catch (error: any) {
    if (error?.response?.status === 403) {
      console.error("You are not authorized to delete lead types.");
    } else {
      handleAxiosError(error, "Failed to delete lead types");
    }
    throw error;
  }
};

/* =====================================================
   IMPORT – EXCEL
===================================================== */

export interface LeadTypeImportResult {
  message: string;
  added: {
    lead_type_name: string;
    lead_type_status: string;
  }[];
  skipped: {
    row: number;
    lead_type_name: string;
    reason: string;
  }[];
}

// ✅ Import Lead Type Excel
export const importLeadTypeExcel = async (
  file: File
): Promise<LeadTypeImportResult> => {
  try {
    const formData = new FormData();
    formData.append("file", file);

    const res = await axios.post(
      `${BASE_URL}/leadtype_import_excel`,
      formData,
      {
        headers: {
          ...getAuthHeaders(),
          "Content-Type": "multipart/form-data",
        },
      }
    );

    toast.success(res.data?.message || "Lead type import completed");
    return res.data;
  } catch (error) {
    handleAxiosError(error, "Failed to import lead types");
    throw error;
  }
};